import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import PresenceManager from '../services/PresenceManager';
import logger from '../utils/logger';

const buildPresenceRecord = (rawPresence = {}) => {
  return {
    presence: rawPresence.presence || 'offline',
    currentlyActive: rawPresence.currentlyActive ?? false,
    lastActiveAgo: typeof rawPresence.lastActiveAgo === 'number' ? rawPresence.lastActiveAgo : null,
    statusMsg: rawPresence.statusMsg || '',
    avatarUrl: rawPresence.avatarUrl || null,
    timestamp: rawPresence.timestamp || Date.now(),
  };
};

export const useUserPresence = () => {
  const client = useMatrixClient();
  const [presenceMap, setPresenceMap] = useState(() => new Map());
  const managerRef = useRef(null);
  const trackedUsersRef = useRef(new Set());

  useEffect(() => {
    if (!client) {
      setPresenceMap(new Map());
      managerRef.current?.destroy?.();
      managerRef.current = null;
      trackedUsersRef.current.clear();
      return undefined;
    }

    const presenceManager = new PresenceManager(client);
    managerRef.current = presenceManager;
    trackedUsersRef.current.clear();

    const subscription = presenceManager.subscribe((updates) => {
      setPresenceMap((prev) => {
        const next = new Map(prev);
        updates.forEach((record, userId) => {
          next.set(userId, buildPresenceRecord(record));
        });
        return next;
      });
    });

    const seedTrackedUsers = () => {
      try {
        const seeded = new Map();

        const users = client.getUsers();
        users.forEach((user) => {
          if (!user?.userId) return;
          trackedUsersRef.current.add(user.userId);
          presenceManager.trackUser(user.userId);

          const initialPresence = presenceManager.getPresence(user.userId) || {
            presence: user.presence,
            currentlyActive: user.currentlyActive,
            lastActiveAgo: user.lastActiveAgo,
            statusMsg: user.presenceStatusMsg,
            avatarUrl: user.avatarUrl,
          };

          seeded.set(user.userId, buildPresenceRecord(initialPresence));
        });

        const selfId = client.getUserId();
        if (selfId) {
          trackedUsersRef.current.add(selfId);
          presenceManager.trackUser(selfId);
          // Note: Our own presence is set by matrixService's Page Visibility API handler
          // We just track it here to show it in the UI

          const selfPresence = presenceManager.getPresence(selfId) || {
            presence: 'online',
            currentlyActive: true,
            lastActiveAgo: 0,
          };
          seeded.set(selfId, buildPresenceRecord(selfPresence));
        }

        setPresenceMap(seeded);
      } catch (error) {
        logger.error('useUserPresence: Failed to seed presence data', error);
      }
    };

    seedTrackedUsers();
    presenceManager.refreshAllPresence();

    return () => {
      subscription?.();
      presenceManager.destroy();
      managerRef.current = null;
      trackedUsersRef.current.clear();
    };
  }, [client]);

  const ensureTracked = useCallback((userId) => {
    if (!userId) return;
    if (trackedUsersRef.current.has(userId)) return;
    if (!managerRef.current) return;

    managerRef.current.trackUser(userId);
    trackedUsersRef.current.add(userId);
  }, []);

  const hasPresence = useCallback((userId) => {
    if (!userId) return false;
    ensureTracked(userId);
    return presenceMap.has(userId);
  }, [presenceMap, ensureTracked]);

  const getPresence = useCallback((userId) => {
    if (!userId) return null;
    ensureTracked(userId);

    const cached = presenceMap.get(userId);
    if (cached) return cached;

    if (managerRef.current) {
      const raw = managerRef.current.getPresence(userId);
      if (raw) {
        return buildPresenceRecord(raw);
      }
    }

    return null;
  }, [presenceMap, ensureTracked]);

  const isOnline = useCallback((userId) => {
    if (!userId) return false;
    ensureTracked(userId);

    if (managerRef.current) {
      return managerRef.current.isOnline(userId);
    }

    const record = presenceMap.get(userId);
    if (!record) return false;
    if (record.presence === 'unknown') return false;
    if (record.presence === 'online') return true;
    return record.currentlyActive === true;
  }, [presenceMap, ensureTracked]);

  return useMemo(() => ({
    presenceMap,
    hasPresence,
    getPresence,
    isOnline,
  }), [presenceMap, hasPresence, getPresence, isOnline]);
};

export default useUserPresence;
